'use client';

import { DeleteOutlined } from '@mui/icons-material';
import {
	Button,
	CircularProgress,
	FormControlLabel,
	FormGroup,
	Grid,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { DataGridPro, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useMutation } from '@parkchirp/api-client';
import { adaptGridValueCallback } from '@parkchirp/ui-library';
import { AccessDeviceInactiveReasonEnum, AccessDeviceTypeDisplayEnum } from 'parkchirp-shared/enums';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { SEND_BARCODE_EMAIL } from '../../../mutations';
import type { SetShowSnackbar } from '../../../types';
import {
	Dayjs,
	datagridFilterPanelAttributes,
	dayjs,
	formatDateForAccessDevices,
	getDayjsFromDeviceDate,
	isOwnerPortal,
} from '../../../utils';

interface AccessDevicesProps {
	newBillingStartDate: Dayjs;
	parkerLoading: boolean;
	currentDate: Date;
	newAccessDevices: any[];
	setNewAccessDevices: (newAccessDevices: any) => any[] | void;
	setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
	isEditMode: boolean;
	newEmail: string;
	parkerId: string;
	setShowSnackbar: SetShowSnackbar;
	tenantSubscriptionId: string;
	tenantAllowedDevices?: string[];
	tenantDevicesLoading?: boolean;
	facilityIntegration: string;
	minStartDate: Dayjs;
	accessSystemCredentials: {
		barcodeRequired?: boolean;
	};
}

export const AccessDevicesDetail: FC<AccessDevicesProps> = ({
	newBillingStartDate,
	parkerLoading,
	currentDate,
	newAccessDevices,
	setNewAccessDevices,
	setHasUnsavedChanges,
	isEditMode,
	newEmail,
	parkerId,
	setShowSnackbar,
	tenantSubscriptionId,
	tenantAllowedDevices,
	tenantDevicesLoading,
	facilityIntegration,
	minStartDate,
	accessSystemCredentials,
}) => {
	const [showAllRows, setShowAllRows] = useState<boolean>(false);
	const [emailRecipient, setEmailRecipient] = useState<string>();
	const [showEmailInput, setShowEmailInput] = useState<boolean>(false);
	const [rowEditState, setRowEditState] = useState<Record<string, boolean>>({});

	useEffect(() => {
		if (!isEditMode) {
			setRowEditState({});
		}
	}, [isEditMode]);

	const deviceRowsToShow = showAllRows ? newAccessDevices : newAccessDevices.filter((row) => row.status === 'Active');

	const deviceTypes = Object.entries(AccessDeviceTypeDisplayEnum).map(([key, value]) => ({ key, value }));

	const filteredDevices = tenantSubscriptionId
		? deviceTypes.filter(({ key }) => tenantAllowedDevices.includes(key))
		: deviceTypes;

	const inactiveReasons = Object.entries(AccessDeviceInactiveReasonEnum).map(([key, value]) => ({ key, value }));

	const defaultActiveDate = newBillingStartDate.isAfter(currentDate, 'day')
		? newBillingStartDate
		: dayjs.utc(currentDate);

	const handleAddDevice = useCallback(() => {
		setNewAccessDevices((prevDevices) => [
			...prevDevices,
			{
				id: uuidv4(),
				type: '',
				deviceId: '',
				status: '',
				dateAdded: formatDateForAccessDevices(currentDate),
				dateActive: defaultActiveDate,
				dateInactive: '',
				inactiveReason: '',
				isNew: true,
				hasInactiveDate: false,
			},
		]);
	}, [setNewAccessDevices, currentDate, defaultActiveDate]);

	const handleDeleteDevice = useCallback(
		(id: GridRowId) => () => {
			setNewAccessDevices((prevDevices) => prevDevices.filter((row) => row.id !== id));
		},
		[setNewAccessDevices],
	);

	const handleKeyDown = useCallback((event) => {
		if (event.key === ' ') {
			event.stopPropagation();
		}
	}, []);

	const getStatus = useCallback(
		(deviceType, activeDate, inactiveDate) => {
			if (deviceType) {
				const deviceDateActive = getDayjsFromDeviceDate(activeDate);
				const deviceDateInactive = getDayjsFromDeviceDate(inactiveDate);

				if (
					deviceDateActive.isAfter(currentDate, 'day') ||
					(deviceDateInactive && deviceDateInactive.isBefore(currentDate, 'day'))
				) {
					return 'Inactive';
				}

				return 'Active';
			}

			return null;
		},
		[currentDate],
	);

	const handleDeviceInfoUpdate = useCallback(
		(id: GridRowId, field: string, value: any) => {
			setNewAccessDevices((prevDevices) =>
				prevDevices.map((device) => {
					if (device.id !== id) {
						return device;
					}
					const updatedDevice = {
						...device,
						[field]: value,
					};

					updatedDevice.status = getStatus(
						updatedDevice.type,
						updatedDevice.dateActive,
						updatedDevice.dateInactive,
					);
					return updatedDevice;
				}),
			);
		},
		[getStatus, setNewAccessDevices],
	);

	useEffect(() => {
		if (newEmail) {
			setEmailRecipient(newEmail);
		}
	}, [newEmail]);

	const showEmailButton = newAccessDevices.some((device) => device.type === 'Barcode') && !isOwnerPortal;

	const handleToggleEmailInput = useCallback(() => {
		setShowEmailInput(!showEmailInput);
		setEmailRecipient(newEmail);
	}, [setShowEmailInput, newEmail, showEmailInput]);

	const [sendBarcodeEmail, { loading: emailLoading }] = useMutation(SEND_BARCODE_EMAIL, {
		onCompleted: () => {
			setShowSnackbar({
				message: 'Email Sent',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			handleToggleEmailInput();
		},
	});

	const emailValidation = useCallback(() => {
		const re = /\S+@\S+\.\S+/;
		return re.test(emailRecipient);
	}, [emailRecipient]);

	const handleEmailBarcode = useCallback(
		async (event) => {
			event.preventDefault();
			if (emailValidation()) {
				try {
					await sendBarcodeEmail({
						variables: {
							parkerId,
							emailAddress: emailRecipient,
							tenantSubscriptionId,
						},
					});
				} catch (error) {
					setShowSnackbar({
						message: error.message,
						severity: 'error',
						autoHideDuration: 5000,
						onClose: () => {
							setShowSnackbar(null);
						},
					});
				}
			} else {
				setShowSnackbar({
					message: 'Invalid Email Address',
					severity: 'error',
					autoHideDuration: 5000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
		},
		[parkerId, setShowSnackbar, sendBarcodeEmail, emailRecipient, emailValidation, tenantSubscriptionId],
	);

	const deviceColumns: GridColDef[] = [
		{
			field: 'type',
			headerName: 'Device Type',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 3,
			renderCell: (params) =>
				isEditMode && params.row.isNew ? (
					<TextField
						select
						value={params.row.type || ''}
						onChange={(e) => {
							setHasUnsavedChanges(true);
							handleDeviceInfoUpdate(params.row.id, 'type', e.target.value);
						}}
						style={{ width: '100%' }}
						data-cy='device-type-select-menu'
					>
						{filteredDevices?.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.value}
							</MenuItem>
						))}
						{filteredDevices?.length === 0 ? (
							<Typography variant='body1' style={{ padding: '10px' }}>
								No configured devices for this subscription.
							</Typography>
						) : null}
					</TextField>
				) : (
					params.row.type || ''
				),
			resizable: false,
			sortable: false,
		},
		{
			field: 'deviceId',
			headerName: 'Device ID',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 4,
			renderCell: (params) => {
				const dataCy = { 'data-cy': 'device-id-input' };
				const textField = (
					<TextField
						onChange={(e) => {
							setHasUnsavedChanges(true);
							handleDeviceInfoUpdate(params.row.id, 'deviceId', e.target.value);
						}}
					/>
				);
				if (isEditMode) {
					if (params.row.isNew) {
						if (params.row.type === 'Barcode') {
							return <TextField disabled value='' inputProps={dataCy} />;
						}
						return (
							<TextField
								value={params.row.deviceId || ''}
								onKeyDown={handleKeyDown}
								onChange={(e) => {
									setHasUnsavedChanges(true);
									handleDeviceInfoUpdate(params.row.id, 'deviceId', e.target.value);
								}}
								inputProps={dataCy}
							/>
						);
					}
					if (!params.row.deviceId || rowEditState[params.row.id]) {
						if (!rowEditState[params.row.id]) {
							rowEditState[params.row.id] = true;
							setRowEditState({ ...rowEditState, [params.row.id]: true });
						}
						return textField;
					}
				}
				return params.row.deviceId || '';
			},
			resizable: false,
			sortable: false,
		},
		{
			field: 'status',
			headerName: 'Status',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 1,
			renderCell: (params) => params.row.status || '',
			resizable: false,
			sortable: false,
		},
		{
			field: 'dateAdded',
			headerName: 'Date Added',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 2,
			renderCell: (params) => dayjs(params.row.dateAdded).utc().format('MM/DD/YYYY') || '',
			resizable: false,
			sortable: false,
		},
		{
			field: 'dateActive',
			headerName: 'Active Date',
			headerAlign: 'center' as const,
			renderCell: (params) => {
				const activeDate = getDayjsFromDeviceDate(params.row.dateActive);
				return (
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker<Dayjs>
							value={activeDate}
							minDate={facilityIntegration === 'GET_MY_PARKING' ? minStartDate : dayjs.utc(currentDate)}
							onChange={(newValue) => {
								setHasUnsavedChanges(true);
								handleDeviceInfoUpdate(params.row.id, 'dateActive', newValue);
							}}
							sx={{ width: '100%' }}
							readOnly={!isEditMode || (!params.row.isNew && activeDate.isSameOrBefore(currentDate))}
							slotProps={{
								textField: {
									inputProps: {
										'data-cy': 'active-date-date-picker',
									},
								},
							}}
						/>
					</LocalizationProvider>
				);
			},
			cellClassName: 'tableCell',
			flex: 3,
			resizable: false,
			sortable: false,
		},
		{
			field: 'dateInactive',
			headerName: 'Inactive Date',
			headerAlign: 'center' as const,
			renderCell: (params) => {
				const inactiveDate = getDayjsFromDeviceDate(params.row.dateInactive);
				const shouldShowDatePicker = params.row.isNew || isEditMode || inactiveDate;

				const activeDate = getDayjsFromDeviceDate(params.row.dateActive);
				if (
					shouldShowDatePicker &&
					!(facilityIntegration === 'GET_MY_PARKING' && params.row.type === 'Barcode')
				) {
					return (
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker<Dayjs>
								value={inactiveDate}
								shouldDisableDate={(day) => day.isBefore(activeDate)}
								onChange={(newValue) => {
									setHasUnsavedChanges(true);
									handleDeviceInfoUpdate(params.row.id, 'dateInactive', newValue);
								}}
								sx={{ width: '100%' }}
								readOnly={
									!isEditMode ||
									(params.row.hasInactiveDate && inactiveDate?.isBefore(dayjs().utc().startOf('day')))
								}
								slotProps={{
									textField: {
										inputProps: {
											'data-cy': 'inactive-date-date-picker',
										},
									},
								}}
							/>
						</LocalizationProvider>
					);
				}
				return null;
			},
			cellClassName: 'tableCell',
			flex: 3,
			resizable: false,
			sortable: false,
		},
		{
			field: 'inactiveReason',
			headerName: 'Reason',
			headerAlign: 'center' as const,
			valueFormatter: adaptGridValueCallback((params) => {
				const { value } = params;
				if (!value) return '';
				const formatted = value?.replace(/_/g, ' ').replace(/ {3}/g, ' - ');
				return formatted;
			}),
			renderCell: (params) => {
				const dataCy = 'reason-select-menu';
				if (isEditMode) {
					if (params.row.dateInactive) {
						return (
							<Select
								value={params.row.inactiveReason || ''}
								onChange={(e) => {
									setHasUnsavedChanges(true);
									params.row.inactiveReason = e.target.value;
									handleDeviceInfoUpdate(params.row.id, 'inactiveReason', e.target.value);
								}}
								style={{ width: '100%' }}
								displayEmpty
								data-cy={dataCy}
							>
								<MenuItem value='' disabled>
									Please select a reason
								</MenuItem>
								{inactiveReasons?.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.value}
									</MenuItem>
								))}
							</Select>
						);
					}
					return <TextField disabled inputProps={{ 'data-cy': dataCy }} />;
				}
				return params.row.dateInactive ? (params.formattedValue as string) : '';
			},
			cellClassName: 'tableCell',
			flex: 3,
			resizable: false,
			sortable: false,
		},
	];

	if (isEditMode || !isOwnerPortal) {
		deviceColumns.push({
			field: 'actions',
			type: 'actions' as const,
			getActions: (params) =>
				params.row.isNew
					? [
							<GridActionsCellItem
								key={params.id}
								icon={<DeleteOutlined />}
								label='Delete'
								onClick={handleDeleteDevice(params.id)}
								data-cy='delete-icon-button'
							/>,
						]
					: [],
			cellClassName: 'actionCell',
			flex: 0.5,
			editable: false,
			resizable: false,
			sortable: false,
		});
	}

	return (
		<Grid className='pca__parkerDetail-devices' item xs={12} sx={{ mt: 1, mb: 1 }}>
			<h2>
				Access Devices{' '}
				{facilityIntegration === 'GET_MY_PARKING' && accessSystemCredentials?.barcodeRequired && <span>*</span>}
			</h2>
			{!isEditMode && (
				<FormGroup>
					<FormControlLabel
						control={
							<Switch
								checked={showAllRows}
								onChange={(event) => setShowAllRows(event.target.checked)}
								data-cy='access-devices-show-inactive-switch'
							/>
						}
						label='Show Inactive Devices'
					/>
				</FormGroup>
			)}
			{(!isEditMode || newAccessDevices.length > 0) && (
				<DataGridPro
					sx={{ mt: 1, mb: 2 }}
					rows={isEditMode ? newAccessDevices : deviceRowsToShow}
					columns={deviceColumns}
					loading={parkerLoading || tenantDevicesLoading}
					slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
					getRowId={(row) => row.id}
					autoHeight
					disableRowSelectionOnClick
					disableColumnReorder
					disableColumnMenu
					hideFooter
				/>
			)}
			{isEditMode && !isOwnerPortal ? (
				<Button
					variant='contained'
					onClick={handleAddDevice}
					sx={{ mt: 1 }}
					size='small'
					data-cy='add-access-device-button'
				>
					Add Access Device
				</Button>
			) : (
				<>
					{showEmailButton && !showEmailInput && (
						<Button variant='contained' sx={{ mt: 1 }} size='small' onClick={() => setShowEmailInput(true)}>
							Email Barcode
						</Button>
					)}
					{showEmailInput && (
						<Grid container alignItems='center' spacing={1} sx={{ mt: 1 }}>
							<Grid item xs={6}>
								<Typography variant='body2' sx={{ fontWeight: 'bold', fontStyle: 'italic' }}>
									{`Enter an email address to send the parker's barcode`}
								</Typography>
								<TextField
									fullWidth
									sx={{ height: '80px', mt: 1 }}
									value={emailRecipient}
									onChange={(e) => setEmailRecipient(e.target.value)}
								/>
							</Grid>
							<Grid item alignItems='center' justifyContent='center' sx={{ ml: 2 }}>
								<Button variant='contained' size='small' color='success' onClick={handleEmailBarcode}>
									{emailLoading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Send'}
								</Button>
							</Grid>
							<Grid item alignItems='center' justifyContent='center'>
								<Button variant='text' size='small' onClick={handleToggleEmailInput}>
									Cancel
								</Button>
							</Grid>
						</Grid>
					)}
				</>
			)}
		</Grid>
	);
};
